// Custom TipTap Node extensions used by the Journal editor (Phase C).
// Kept small & schema-only so DOMPurify on the public page can serialize the
// output back into HTML without a JS round-trip.
import { Node, Mark, mergeAttributes } from "@tiptap/core";

// ----------------------------------------------------------------------------
// AccentMark — wraps selected text in `<span class="accent">…</span>` so the
// site-wide `.accent` CSS rule (terracotta #c1521f) tints hook phrases inside
// headlines and body copy. Behaves like a standard inline Mark (bold/italic),
// so pressing the toolbar button toggles it on and off around the selection.
// ----------------------------------------------------------------------------
export const AccentMark = Mark.create({
  name: "accent",
  parseHTML() {
    return [
      {
        tag: "span",
        getAttrs: (el) =>
          (el.getAttribute("class") || "").split(/\s+/).includes("accent") ? {} : false,
      },
    ];
  },
  renderHTML({ HTMLAttributes }) {
    return ["span", mergeAttributes(HTMLAttributes, { class: "accent" }), 0];
  },
  addCommands() {
    return {
      toggleAccent:
        () =>
        ({ commands }) =>
          commands.toggleMark(this.name),
    };
  },
});

// ----------------------------------------------------------------------------
// PullQuote — a decorative editorial callout distinct from a regular quote.
// Renders as:  <aside data-pull-quote="true" class="pull-quote"> … </aside>
// Behaves like a paragraph (inline content) so users can type freely into it
// and toggle it on/off from the toolbar.
// ----------------------------------------------------------------------------
export const PullQuote = Node.create({
  name: "pullQuote",
  group: "block",
  content: "inline*",
  defining: true,
  parseHTML() {
    return [{ tag: "aside[data-pull-quote]" }];
  },
  renderHTML({ HTMLAttributes }) {
    return [
      "aside",
      mergeAttributes(HTMLAttributes, {
        "data-pull-quote": "true",
        class: "pull-quote",
      }),
      0,
    ];
  },
  addCommands() {
    return {
      togglePullQuote:
        () =>
        ({ commands }) =>
          commands.toggleNode(this.name, "paragraph"),
    };
  },
});

// ----------------------------------------------------------------------------
// ImageFigure — atomic block that pairs an image with an optional caption.
// Serialized to:
//   <figure data-image-figure="true">
//     <img src=… alt=… />
//     <figcaption>…</figcaption>   (omitted when caption is empty)
//   </figure>
// The node stores src / alt / caption as attributes so the caption survives
// through DOMPurify without extra allowlist tweaks.
// ----------------------------------------------------------------------------
export const ImageFigure = Node.create({
  name: "imageFigure",
  group: "block",
  atom: true,
  draggable: true,
  addAttributes() {
    return {
      src: { default: null },
      alt: { default: "" },
      caption: { default: "" },
    };
  },
  parseHTML() {
    return [
      {
        tag: "figure[data-image-figure]",
        getAttrs: (el) => {
          const img = el.querySelector("img");
          const cap = el.querySelector("figcaption");
          return {
            src: img?.getAttribute("src") || null,
            alt: img?.getAttribute("alt") || "",
            caption: cap?.textContent || "",
          };
        },
      },
    ];
  },
  renderHTML({ node }) {
    const children = [
      [
        "img",
        {
          src: node.attrs.src,
          alt: node.attrs.alt || "",
          class: "w-full rounded-xl border border-[#EAE7E0]",
        },
      ],
    ];
    if (node.attrs.caption) {
      children.push([
        "figcaption",
        {
          class:
            "mt-2 text-center text-sm text-[#8A8F99] italic font-serif-display",
        },
        node.attrs.caption,
      ]);
    }
    return [
      "figure",
      { "data-image-figure": "true", class: "my-6" },
      ...children,
    ];
  },
  addCommands() {
    return {
      setImageFigure:
        (attrs) =>
        ({ commands }) =>
          commands.insertContent({ type: this.name, attrs }),
    };
  },
});
